import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Alert, Button, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";

import { DiscrepancyService } from "@services/ingest/discrepancyService";
import type { Correction } from "@models/Discrepancy.model";
import { Paths } from "@/paths";
import { formatDay } from "./DataIssuesPage";
import EditPeopleDialog from "./EditPeopleDialog";

const service = new DiscrepancyService();

const WHAT = (c: Correction) => {
  if (c.action === "custom_fix") return `Custom fix · ${c.script}`;
  if (c.action === "set_value") return `Fix a value · ${c.attr_key} = ${c.value ?? "blank"}`;
  if (c.action === "stop_serving") return "Stop serving";
  return c.allow_return ? "Remove · let back in" : "Remove · keep out";
};

interface Props {
  /** One discrepancy's corrections, or a population's. */
  discrepancyId?: number;
  populationSegmentId?: number;
  /** Bump to reload, e.g. after a correction is added. */
  reloadKey?: number;
}

/*
    Corrections, End and Edit people (MPN-2009, MPN-2125). Never edited in place: End stops
    it from the next file on and keeps it in the history; Edit people ends it and adds one
    with the new list that says which it replaced.
*/
export default function CorrectionsTable({ discrepancyId, populationSegmentId, reloadKey }: Props) {
  const [rows, setRows] = useState<Correction[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Correction | null>(null);

  const load = useCallback(() => {
    service.corrections({ discrepancyId, populationSegmentId })
      .then(setRows)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load corrections"));
  }, [discrepancyId, populationSegmentId]);

  useEffect(load, [load, reloadKey]);

  const end = async (c: Correction) => {
    setError(null);
    try {
      await service.revokeCorrection(c.id);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not end it");
    }
  };

  if (!rows) return error ? <Alert severity="error">{error}</Alert> : null;
  if (rows.length === 0) {
    return <Typography sx={{ color: "#64748b", fontSize: 13 }}>No corrections yet.</Typography>;
  }
  return (
    <>
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>What</TableCell>
            {!discrepancyId && <TableCell>Why</TableCell>}
            <TableCell>People</TableCell>
            <TableCell>From</TableCell>
            <TableCell>Status</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((c) => (
            <TableRow key={c.id}>
              <TableCell>{c.id}</TableCell>
              <TableCell>
                {WHAT(c)}
                {c.action === "custom_fix" && (
                  <Typography fontSize={12} color="#64748b">
                    {c.note}
                    {Object.keys(c.changes).length > 0 && ` · ${Object.entries(c.changes)
                      .map(([label, n]) => `${label} ${n.before.toLocaleString()} → ${(n.after ?? 0).toLocaleString()}`).join(", ")}`}
                  </Typography>
                )}
              </TableCell>
              {!discrepancyId && (
                <TableCell>
                  <Link to={`${import.meta.env.BASE_URL}${Paths.DATA_ISSUES}/${c.discrepancy_id}`}>
                    {c.discrepancy_reason}
                  </Link>
                </TableCell>
              )}
              <TableCell>
                {c.people.toLocaleString()}
                {c.returned > 0 && ` (${c.returned.toLocaleString()} back)`}
                {c.supersedes_id && <Typography fontSize={12} color="#64748b">replaces #{c.supersedes_id}</Typography>}
                {c.superseded_by_id && <Typography fontSize={12} color="#64748b">replaced by #{c.superseded_by_id}</Typography>}
              </TableCell>
              <TableCell>
                {c.start_file ? `${c.start_file.file_name ?? "File"} (rebuilt)` : "Next file"} · {formatDay(c.effective_from)}
              </TableCell>
              <TableCell>
                {c.action === "custom_fix"
                  ? `Ran ${formatDay(c.create_date)}${c.created_by ? ` · ${c.created_by}` : ""}`
                  : c.revoked_at
                    ? `Ended ${formatDay(c.revoked_at)}${c.revoked_by ? ` · ${c.revoked_by}` : ""}`
                    : `Active since ${formatDay(c.create_date)}`}
              </TableCell>
              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                {!c.revoked_at && c.action !== "custom_fix" && (
                  <>
                    <Button size="small" onClick={() => setEditing(c)} sx={{ textTransform: "none" }}>Edit people</Button>
                    <Button size="small" onClick={() => end(c)} sx={{ textTransform: "none" }}>End</Button>
                  </>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {editing && (
        <EditPeopleDialog correction={editing} onClose={() => setEditing(null)}
          onReplaced={() => { setEditing(null); load(); }} />
      )}
    </>
  );
}
